import { AccountType, Organization } from "types/orgTypes";
import { accountTypeValues } from "@constants/orgConstants";
import { withDemoAndDistributorTypes } from "@constants/accountTypeOptions";

export const ORGANIZATION_LIST_PAGE_SIZE = 25;
export const ORGANIZATION_SEARCH_MAX_LENGTH = 64;

export type DeviceFilter = "any" | "with" | "without";

export interface OrganizationListFilters {
  accountType: AccountType | null;
  devices: DeviceFilter;
  distId: string | null;
}

export const EMPTY_ORGANIZATION_FILTERS: OrganizationListFilters = {
  accountType: null,
  devices: "any",
  distId: null,
};

export type OrganizationListRow = Organization;

export interface OrganizationListResponse {
  data: OrganizationListRow[];
  total: number;
  page: number;
  limit: number;
  lastPage: number;
}

export function toAccountType(value: string | null): AccountType | null {
  return (withDemoAndDistributorTypes(accountTypeValues, true) as readonly string[]).includes(
    value ?? "",
  )
    ? (value as AccountType)
    : null;
}

export function hasActiveFilters(filters: OrganizationListFilters, search: string): boolean {
  return (
    search.trim() !== "" ||
    filters.accountType !== null ||
    filters.devices !== "any" ||
    filters.distId !== null
  );
}

export function buildOrganizationListQuery(
  filters: OrganizationListFilters,
  search: string,
  page: number,
  limit: number = ORGANIZATION_LIST_PAGE_SIZE,
): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = { page, limit };
  const term = search.trim().slice(0, ORGANIZATION_SEARCH_MAX_LENGTH);
  if (term) {
    query.search = encodeURIComponent(term);
  }
  if (filters.accountType) {
    query.accountType = encodeURIComponent(filters.accountType);
  }
  if (filters.devices === "with") {
    query.hasDevices = true;
  } else if (filters.devices === "without") {
    query.hasDevices = false;
  }
  if (filters.distId) {
    query.distId = encodeURIComponent(filters.distId);
  }
  return query;
}

export function parseOrganizationListResponse(body: unknown): OrganizationListResponse | null {
  const raw = body as Partial<OrganizationListResponse> | null | undefined;
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.data)) return null;
  if (typeof raw.total !== "number" || !Number.isFinite(raw.total) || raw.total < 0) return null;
  const data = raw.data;
  const total = raw.total;
  const limit =
    typeof raw.limit === "number" && raw.limit > 0 ? raw.limit : ORGANIZATION_LIST_PAGE_SIZE;
  const page = typeof raw.page === "number" && raw.page > 0 ? raw.page : 1;
  return {
    data,
    total,
    page,
    limit,
    lastPage: Math.max(1, Math.ceil(total / limit)),
  };
}
